import { Alert, Button, Divider, Fieldset, Group, List, Select, Space, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconInfoCircle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import Dropzone from "@/components/admin/Dropzone";
import FileUploadCard from "@/components/admin/FileUploadCard";

type FormInputs = {
  defaultMaxChildren: number[];
  maxClassifications: number[];
};

type Props = {
  handleUpload: (
    defaultMaxChildren: number[],
    maxClassifications: number[],
    file: File | null,
  ) => void;
  isUploading: boolean;
  maxLevel: number;
  errorMessages: string[];
};

const CategoryUploader: React.FC<Props> = ({ handleUpload, isUploading, maxLevel, errorMessages }) => {
  const [file, setFile] = useState<File | null>(null);

  const form = useForm<FormInputs>({
    initialValues: {
      defaultMaxChildren: Array(maxLevel).fill(3),
      maxClassifications: Array(maxLevel).fill(1),
    },
    validateInputOnChange: true,
  });

  // maxLevelが変更されたときにフォームの値を更新
  useEffect(() => {
    form.setValues({
      defaultMaxChildren: Array(maxLevel).fill(3),
      maxClassifications: Array(maxLevel).fill(1),
    });
  }, [maxLevel]);

  const onSubmit = async () => {
    const { defaultMaxChildren, maxClassifications } = form.values;
    await handleUpload(defaultMaxChildren, maxClassifications, file);
    setFile(null);
  };

  const handleUpdateMaxClassifications = (index: number, value: string | null) => {
    if (!value) return;

    const values = index === maxLevel - 1
      // 課題の場合は最後だけ更新
      ? form.values.maxClassifications.toSpliced(index, 1, Number(value))
      // カテゴリの場合は上層にも同じ値を設定
      : [...Array(maxLevel - 1).fill(Number(value)), form.values.maxClassifications[maxLevel - 1]];

    form.setFieldValue("maxClassifications", values);
  };

  return (
    <>
      {errorMessages.length > 0 && (
        <Alert variant="light" color="red" title="エラー詳細" icon={<IconInfoCircle />} mb={20}>
          <List>
            {errorMessages.map((message) => <List.Item key={message}>{message}</List.Item>)}
          </List>
        </Alert>
      )}
      <Dropzone
        title="カテゴリファイルアップロード"
        onDrop={(files) => setFile(files[0])}
        acceptableMimeTypes={["csv", "xlsx"]}
      />
      {file && (
        <>
          <Space h="10px" />
          <FileUploadCard file={file} onDelete={() => setFile(null)} />
        </>
      )}
      <Divider
        mt={30}
        label={<Text size="lg">カテゴリ設定</Text>}
        labelPosition="left"
      />
      <Stack mt={15}>
        <Text size="sm" fw={700}>カテゴリの階層数 : {maxLevel - 1}</Text>
      </Stack>
      <Fieldset legend="デフォルトの課題ラベル生成数" mt={20}>
        <Group mt={3}>
          <Select
            data-testid="default-max-children-select"
            allowDeselect={false}
            maxDropdownHeight={200}
            style={{ width: "7rem" }}
            data={Array.from({ length: 20 }, (_, i) => `${i + 1}`)}
            value={`${form.values.defaultMaxChildren[maxLevel - 1]}`}
            onChange={(value) => {
              if (!value) return;

              form.setFieldValue(
                "defaultMaxChildren",
                form.values.defaultMaxChildren.toSpliced(maxLevel - 1, 1, Number(value)),
              );
            }}
          />
        </Group>
      </Fieldset>
      <Fieldset legend="分類数" mt={20}>
        <Group mt={3}>
          {[{ kind: "カテゴリ", index: maxLevel - 2 }, { kind: "課題", index: maxLevel - 1 }].map((opt) => (
            <Select
              key={opt.kind}
              data-testid={`${opt.kind}-classification-select`}
              label={`${opt.kind}の分類数`}
              allowDeselect={false}
              maxDropdownHeight={200}
              style={{ width: "7rem" }}
              data={Array.from({ length: 5 }, (_, i) => `${i + 1}`)}
              value={`${form.values.maxClassifications[opt.index]}`}
              onChange={(value) => handleUpdateMaxClassifications(opt.index, value)}
            />
          ))}
        </Group>
      </Fieldset>
      <Group justify="flex-end">
        <Button
          data-testid="upload-button"
          mt={20}
          size="md"
          style={{ width: "14rem" }}
          onClick={onSubmit}
          loading={isUploading}
          disabled={file === null || isUploading}
        >
          アップロード
        </Button>
      </Group>
    </>
  );
};

export default CategoryUploader;
